import { zodResolver } from '@hookform/resolvers/zod';
import DurationPicker from 'react-duration-picker';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Form } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { MultiSelect } from '@/modules/common/components/multi-select';
import { OptionType } from '@/modules/common/types';

import { InstanceFormSchema } from '../validations';

type InstanceFormSchemaType = z.infer<typeof InstanceFormSchema>;

interface InstanceFormProps {
  disabled?: boolean;
  isLoading?: boolean;
  status?: string;
  configOptions: OptionType[];
  defaultValues?: InstanceFormSchemaType;
  onSubmit?: (data: InstanceFormSchemaType) => Promise<void>;
}

export function InstanceForm({
  disabled,
  isLoading,
  status,
  onSubmit,
  configOptions,
  defaultValues,
}: InstanceFormProps) {
  const form = useForm({
    resolver: zodResolver(InstanceFormSchema),
    defaultValues,
  });

  const handleFormSubmit = async () => {
    const data = form.getValues();

    await onSubmit?.(data);
    form.reset();
  };

  const onDurationChange = (field, duration: DurationPicker.Duration) => {
    const durationInMinutes = duration.hours * 60 + duration.minutes;
    field.onChange(durationInMinutes);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleFormSubmit)}
        className="flex flex-col gap-4"
      >
        <div className="gap-4 flex flex-col">
          <div className="grid grid-cols-1 gap-6">
            <FormField
              control={form.control}
              name="instanceCount"
              render={({ field }) => (
                <FormItem className="max-w-[200px]">
                  <FormLabel>Number of Instances</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Number of instances"
                      type="number"
                      min={1}
                      max={100}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <MultiSelect
              name="configs"
              label="Configs"
              className="max-w-[200px]"
              control={form.control}
              placeholder="Select configs"
              options={configOptions}
            />

            <FormField
              control={form.control}
              name="duration"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Duration</FormLabel>
                  <FormControl>
                    <DurationPicker
                      onChange={(duration) => onDurationChange(field, duration)}
                      initialDuration={{ hours: 1, minutes: 0, seconds: 0 }}
                      maxHours={100}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <Button
            type="submit"
            disabled={disabled}
            className="mt-4 max-w-[200px]"
            isLoading={isLoading}
          >
            {Number(defaultValues?.instanceCount) > 1 && status !== 'Offline'
              ? 'Update'
              : 'Launch'}
          </Button>
          {disabled && (
            <p className="text-sm text-red-500">
              You cannot update instances while a batch is "Updating" or
              "Creating" status
            </p>
          )}
        </div>
      </form>
    </Form>
  );
}
